import { FieldOptionBase } from 'module/api/field/types';
import { byLabel } from 'module/helpers/comparators/by-label';
import { TranslationKey, useTranslation } from 'module/hooks/use-translation';
import {
  MultiSelectField,
  MultiUserField,
  SingleSelectField,
  SingleUserField,
} from 'module/types/filter-field/field/filterable-field';
import { SelectFilterOperationType } from 'module/types/filter-field/filter/select';
import { SelectMode } from 'module/ui/components/atoms/select';
import { snakeCase } from 'lodash';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { getControlledSelectBasedEditor } from '../../field-editors/components/controlled-select-based-editor';
import { TriggerContent } from '../../field-editors/components/controlled-select-based-editor-trigger-content';
import { EditorComponents } from '../../field-editors/types';

interface Props {
  field:
    | SingleSelectField
    | MultiSelectField
    | SingleUserField
    | MultiUserField;
  filterType:
    | SelectFilterOperationType.IsNoneOf
    | SelectFilterOperationType.IsNot
    | SelectFilterOperationType.Is
    | SelectFilterOperationType.IsOneOf;
  options: FieldOptionBase<number>[];
  showDoneButton?: boolean;
  value: number[];
  components: EditorComponents;
  onChange: (next: number[]) => void;
  type: SelectMode;
  placeholder?: ReactNode;
  show?: number;
}

const Editor = getControlledSelectBasedEditor<true, number>(
  'multi-select-editor',
  'editors.multi-select.select',
);

export const MultiSelectEditor: FC<React.PropsWithChildren<Props>> = ({
  field,
  value,
  filterType,
  options,
  showDoneButton,
  components: { TriggerOption, ...selectComponents },
  onChange,
  type,
  show,
  placeholder,
}) => {
  const title = useTranslation('task_list.filters.messages.editor_title', {
    field: field.fieldLabel,
    operation: useTranslation(
      `task_list.filters.${snakeCase(filterType)}` as TranslationKey,
    ),
  });

  const [latest, setLatest] = useState(value);

  const onClose = useCallback(() => {
    onChange(latest);
  }, [latest, onChange]);

  useEffect(() => {
    setLatest(value);
  }, [value, setLatest]);

  const items = (value ?? [])
    .map((x) => options.find((o) => o.value === x)!)
    .filter(Boolean)
    .sort(byLabel)
    .map((x) => x as FieldOptionBase<number>)
    .map((x) => <TriggerOption key={x.value} option={x} />);

  return (
    <Editor
      showDoneButton={showDoneButton}
      title={title}
      components={selectComponents}
      isMulti
      options={options}
      value={latest
        .map((v) => options.find((o) => o.value === v))
        .filter((x) => x != null)
        .map((x) => x!)
        .sort(byLabel)}
      onChange={(latest_options?: FieldOptionBase<number>[]) =>
        setLatest((latest_options ?? []).sort(byLabel).map((x) => x.value))
      }
      triggerContent={
        <TriggerContent items={items} placeholder={placeholder} show={show} />
      }
      onClose={onClose}
      type={type}
    />
  );
};
